import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import {
  BxBreadcrumb,
  BxCardComponent,
  BxTabComponent,
  BxTabGroupComponent,
  SvgIconComponent,
  injectBxIcons,
  building05Icon,
  markerPin01Icon,
} from '@beyonnex/bx-angular-ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ROUTES } from '../../app.models';
import { GEO_LOCATION_SUBJECT, type GeoLocationModalData } from '../../components/modals/geo-location-modal/geo-location-modal.models';
import { GeoLocationModalService } from '../../components/modals/geo-location-modal/geo-location-modal.service';
import { ProductStatusBadgeComponent } from '../../components/product-status-badge/product-status-badge.component';
import { formatAddress } from '../../models/foundation.models';
import { PROPERTY_STATUS_SEVERITY } from '../../services/property-status/property-status.service.models';
import { AttributesPanelComponent } from '../device-details/device-attributes/attributes-panel/attributes-panel.component';
import { AttributeLink, AttributesPanelData } from '../device-details/device-attributes/attributes-panel/attributes-panel.models';
import { PageComponent } from '../page/page.component';
import { PropertyDetailsStore } from './property-details.store';
import { PropertyDeviceListComponent } from './property-device-list/property-device-list.component';
import { PropertyGatewayListComponent } from './property-gateway-list/property-gateway-list.component';
import { PropertyProductStatusComponent } from './property-product-status/property-product-status.component';
import { PropertyStatusMatrixComponent } from './property-status-matrix/property-status-matrix.component';
import { PropertyTopologyComponent } from './property-topology/property-topology.component';

@Component({
  selector: 'bxi-property-details',
  imports: [
    PageComponent,
    BxCardComponent,
    AttributesPanelComponent,
    SvgIconComponent,
    TranslatePipe,
    BxTabGroupComponent,
    BxTabComponent,
    PropertyDeviceListComponent,
    PropertyGatewayListComponent,
    PropertyProductStatusComponent,
    PropertyStatusMatrixComponent,
    PropertyTopologyComponent,
    ProductStatusBadgeComponent,
  ],
  providers: [PropertyDetailsStore],
  templateUrl: './property-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyDetailsComponent {
  private readonly store = inject(PropertyDetailsStore);
  private readonly translate = inject(TranslateService);
  private readonly geoLocationModal = inject(GeoLocationModalService);

  protected readonly property = toSignal(this.store.property$);
  protected readonly propertyStatus = toSignal(this.store.propertyStatus$);

  /** The property level carries a status name rather than a number, so the badge resolves it through this. */
  protected readonly severityByStatus = PROPERTY_STATUS_SEVERITY;

  protected readonly billingUnitReference = computed(() => this.property()?.attributes?.billingUnitReference ?? '');
  protected readonly pageTitle = computed(() => `${this.translate.instant('thing.type.PROPERTY')} ${this.billingUnitReference()}`);
  private readonly address = computed(() => formatAddress(this.property()?.getAddress()));
  private readonly geoLocation = computed(() => this.property()?.getResolvedGeoLocation());
  private readonly canChangeGeoLocation = toSignal(this.store.canChangeGeoLocation$, { initialValue: false });
  protected readonly activeTabId = signal<string>('devices');

  protected readonly breadcrumbOptions = computed<BxBreadcrumb[]>(() => [
    { label: this.translate.instant('navigation.properties'), route: `/${ROUTES.PROPERTIES}` },
    { label: this.billingUnitReference() },
  ]);

  protected readonly basicInformation = computed<AttributesPanelData>(() => {
    const attributes: AttributesPanelData['attributes'] = [
      { label: this.translate.instant('attributes.billingUnitReference'), value: this.billingUnitReference() },
    ];

    const geoLocation = this.geoLocation();
    if (this.address()) {
      attributes.push({
        label: this.translate.instant('system.address'),
        value: this.address(),
        wrap: true,
      });
    }
    attributes.push(
      geoLocation
        ? { label: this.translate.instant('system.geoLocation'), link: this.viewGeoLocationLink() }
        : { label: this.translate.instant('system.geoLocation'), value: this.translate.instant('geoLocation.notDetermined') },
    );

    return {
      title: this.translate.instant('thing.attributes.basicInformation'),
      attributes,
    };
  });

  private viewGeoLocationLink(): AttributeLink {
    return { label: this.translate.instant('geoLocation.view'), icon: 'marker-pin-01', action: (): void => this.openGeoLocationModal() };
  }

  private openGeoLocationModal(): void {
    const property = this.property();
    const geoLocation = this.geoLocation();
    if (!property || !geoLocation) {
      return;
    }

    const data: GeoLocationModalData = {
      thingId: property.thingId,
      subject: GEO_LOCATION_SUBJECT.PROPERTY,
      address: this.address(),
      geoLocation,
      canEdit: this.canChangeGeoLocation(),
    };
    // Nothing to do on close: the property is streamed over SSE, so a saved geolocation arrives on its own.
    this.geoLocationModal.open(data).subscribe();
  }

  constructor() {
    injectBxIcons([building05Icon, markerPin01Icon]);

    inject(ActivatedRoute)
      .queryParamMap.pipe(takeUntilDestroyed())
      .subscribe((params) => {
        const thingId = params.get('thingId');
        if (thingId) {
          this.store.initialize(thingId);
        }
      });
  }
}
